import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { restaurantsAPI, restaurantTasksAPI, usersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, Plus, Building, Pencil, Trash2, MapPin, LayoutGrid, CalendarDays, Columns } from 'lucide-react';
import { toast } from 'sonner';
import RestaurantFormModal from '../components/RestaurantFormModal';
import RestaurantTaskModal from '../components/RestaurantTaskModal';
import { RestaurantTaskFilters, RestaurantTaskBoard, RestaurantTaskCalendar, filterRestaurantTasks } from '../components/RestaurantTaskViews';
import { RESTAURANT_STATUSES, RESTAURANT_STATUS_MAP } from '../lib/restaurantConstants';
import { formatCurrency } from '../lib/format';
import { canManageRestaurants } from '../lib/roles';

function MetricCard({ label, value, dot, accent, testId }) {
  return (
    <div className="bg-white border border-[#E0E0E0] rounded-lg p-3 sm:p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        {dot && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: dot }} />}
        <p className="text-xs text-[#666666] font-medium">{label}</p>
      </div>
      <p className="text-xl font-bold" style={{ color: accent || '#111111' }}>{value}</p>
    </div>
  );
}

function ModuleStatsGrid({ modules }) {
  const active = modules.filter(m => m.total_clients > 0);
  const list = active.length > 0 ? active : modules;
  if (list.length === 0) return null;
  return (
    <div>
      <h3 className="text-sm font-semibold text-[#111111] mb-2">Modules</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map(m => (
          <div key={m.id} className="bg-white border border-[#E0E0E0] rounded-lg p-4" data-testid={`module-stat-${m.id}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="font-semibold text-[#111111] truncate">{m.name}</p>
              <Badge variant="secondary" className="text-xs flex-shrink-0">{m.total_clients} clients</Badge>
            </div>
            <div className="grid grid-cols-4 gap-1 mt-3">
              {RESTAURANT_STATUSES.map(s => (
                <div key={s.value} className="text-center">
                  <p className="text-sm font-bold text-[#111111]" data-testid={`module-stat-${m.id}-${s.value}`}>{m.counts?.[s.value] ?? 0}</p>
                  <p className="text-[10px] text-[#999999] truncate" title={s.label}>{s.label}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F0F0F0]">
              <div>
                <p className="text-[10px] text-[#999999] uppercase tracking-wide">MRR</p>
                <p className="text-sm font-bold text-[#059669]" data-testid={`module-mrr-${m.id}`}>{formatCurrency(m.mrr)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-[#999999] uppercase tracking-wide">ARR</p>
                <p className="text-sm font-bold text-[#111111]" data-testid={`module-arr-${m.id}`}>{formatCurrency(m.arr)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RestaurantCard({ restaurant, canManage, onOpen, onEdit, onDelete, onDragStart }) {
  return (
    <div
      draggable={canManage}
      onDragStart={(e) => onDragStart(e, restaurant)}
      onClick={() => onOpen(restaurant)}
      className="bg-white border border-[#E0E0E0] rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow"
      data-testid={`restaurant-card-${restaurant.id}`}
    >
      <div className="flex items-start gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-[#CFFF40] flex items-center justify-center flex-shrink-0">
          <Building size={16} className="text-[#101010]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm text-[#111111] truncate">{restaurant.name}</p>
          <p className="text-[11px] text-[#999999] flex items-center gap-1 mt-0.5">
            <MapPin size={10} />
            <span className="truncate" data-testid={`restaurant-location-${restaurant.id}`}>{restaurant.location || 'No location'}</span>
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-[#F0F0F0]">
        <div>
          <p className="text-[10px] text-[#999999] uppercase tracking-wide">MRR</p>
          <p className="text-sm font-bold text-[#059669]" data-testid={`restaurant-mrr-${restaurant.id}`}>{formatCurrency(restaurant.mrr)}</p>
        </div>
        <div className="flex items-center gap-1">
          <Badge variant="secondary" className="text-[10px]">{restaurant.module_count} mod</Badge>
          {canManage && (
            <>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onEdit(restaurant); }} data-testid={`edit-restaurant-${restaurant.id}`}>
                <Pencil size={12} className="text-[#666666]" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onDelete(restaurant); }} data-testid={`delete-restaurant-${restaurant.id}`}>
                <Trash2 size={12} className="text-red-500" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RestaurantsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = canManageRestaurants(user);

  const [restaurants, setRestaurants] = useState([]);
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draggedRestaurant, setDraggedRestaurant] = useState(null);

  const [taskView, setTaskView] = useState('board');
  const [taskModal, setTaskModal] = useState(undefined);
  const [filterType, setFilterType] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [selectedRestaurants, setSelectedRestaurants] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [rRes, sRes, tRes, uRes] = await Promise.all([
        restaurantsAPI.getAll(),
        restaurantsAPI.getStats(),
        restaurantTasksAPI.getAll(),
        usersAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setRestaurants(rRes.data);
      setStats(sRes.data);
      setTasks(tRes.data);
      setUsers(uRes.data);
    } catch (err) {
      toast.error('Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const locations = useMemo(
    () => Array.from(new Set(restaurants.map(r => (r.location || '').trim()).filter(Boolean))).sort(),
    [restaurants]
  );

  const filteredRestaurants = useMemo(() => restaurants.filter(r => {
    const q = searchQuery.toLowerCase();
    if (q && !r.name.toLowerCase().includes(q) && !(r.location || '').toLowerCase().includes(q)) return false;
    if (filterLocation && (r.location || '').trim() !== filterLocation) return false;
    return true;
  }).sort((a, b) => (b.mrr || 0) - (a.mrr || 0) || a.name.localeCompare(b.name)), [restaurants, searchQuery, filterLocation]);

  const visibleTasks = useMemo(
    () => filterRestaurantTasks(tasks, { filterType, filterAssignee, selectedRestaurants }),
    [tasks, filterType, filterAssignee, selectedRestaurants]
  );

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? This cannot be undone.`)) return;
    try {
      await restaurantsAPI.delete(r.id);
      toast.success('Restaurant deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleStatusDrop = async (e, status) => {
    e.preventDefault();
    if (!draggedRestaurant || draggedRestaurant.status === status) { setDraggedRestaurant(null); return; }
    try {
      await restaurantsAPI.update(draggedRestaurant.id, { status });
      toast.success(`${draggedRestaurant.name} → ${RESTAURANT_STATUS_MAP[status].label}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update status');
    }
    setDraggedRestaurant(null);
  };

  const handleTaskStatusChange = async (task, status) => {
    try {
      await restaurantTasksAPI.update(task.id, { status });
      toast.success('Task moved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move task');
    }
  };

  if (loading) {
    return (
      <DashboardLayout pageTitle="Restaurants">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#CFFF40] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Restaurants">
      <div className="space-y-6" data-testid="restaurants-page">

        {/* Key metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="restaurant-metrics">
          <MetricCard label="Total" value={stats?.total ?? 0} testId="metric-total" />
          {RESTAURANT_STATUSES.map(s => (
            <MetricCard key={s.value} label={s.label} value={stats?.by_status?.[s.value] ?? 0} dot={s.dot} testId={`metric-${s.value}`} />
          ))}
          <MetricCard label="MRR (paying)" value={formatCurrency(stats?.total_mrr)} accent="#059669" testId="metric-mrr" />
          <MetricCard label="ARR (paying)" value={formatCurrency(stats?.total_arr)} accent="#059669" testId="metric-arr" />
        </div>

        <ModuleStatsGrid modules={stats?.modules || []} />

        {/* Tabs */}
        <Tabs defaultValue="restaurants" className="w-full">
          <TabsList className="bg-white border border-[#E0E0E0]">
            <TabsTrigger value="restaurants" data-testid="tab-restaurants">Restaurants</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
          </TabsList>

          <TabsContent value="restaurants" className="mt-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white border border-[#E0E0E0] rounded-lg p-3">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" size={16} />
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-full sm:w-56"
                    data-testid="search-restaurants"
                  />
                </div>
                <select
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                  className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm bg-white"
                  data-testid="filter-location"
                >
                  <option value="">All Locations</option>
                  {locations.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
                <span className="text-sm text-[#666666] self-center">{filteredRestaurants.length} restaurants</span>
              </div>
              {canManage && (
                <Button className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white flex-shrink-0" onClick={() => { setEditing(null); setShowModal(true); }} data-testid="add-restaurant-btn">
                  <Plus size={16} className="mr-2" /> Add Restaurant
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="restaurant-kanban">
              {RESTAURANT_STATUSES.map(s => {
                const colItems = filteredRestaurants.filter(r => r.status === s.value);
                const colMrr = colItems.reduce((sum, r) => sum + (r.mrr || 0), 0);
                return (
                  <div
                    key={s.value}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                    onDrop={(e) => handleStatusDrop(e, s.value)}
                    data-testid={`restaurant-column-${s.value}`}
                  >
                    <div className="bg-[#F5F5F5] rounded-t-lg px-3 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.dot }} />
                        <span className="font-medium text-xs text-[#111111] uppercase tracking-wide truncate">{s.label}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {colMrr > 0 && <span className="text-[10px] text-[#059669] font-semibold">{formatCurrency(colMrr)}</span>}
                        <Badge variant="secondary" className="text-xs">{colItems.length}</Badge>
                      </div>
                    </div>
                    <div className="bg-[#F5F5F5] rounded-b-lg p-2 min-h-[140px] space-y-2">
                      {colItems.map(r => (
                        <RestaurantCard
                          key={r.id}
                          restaurant={r}
                          canManage={canManage}
                          onOpen={(item) => navigate(`/restaurants/${item.id}`)}
                          onEdit={(item) => { setEditing(item); setShowModal(true); }}
                          onDelete={handleDelete}
                          onDragStart={(e, item) => { setDraggedRestaurant(item); e.dataTransfer.effectAllowed = 'move'; }}
                        />
                      ))}
                      {colItems.length === 0 && (
                        <div className="flex items-center justify-center h-20 text-xs text-[#999999]">Empty</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="tasks" className="mt-4 space-y-4">
            <RestaurantTaskFilters
              filterType={filterType} setFilterType={setFilterType}
              filterAssignee={filterAssignee} setFilterAssignee={setFilterAssignee}
              selectedRestaurants={selectedRestaurants} setSelectedRestaurants={setSelectedRestaurants}
              users={users}
              restaurants={restaurants}
              showRestaurantFilter
              count={visibleTasks.length}
              onNewTask={canManage ? () => setTaskModal(null) : undefined}
            />

            <div className="flex items-center gap-1 bg-white border border-[#E0E0E0] rounded-lg p-1 w-fit">
              <button
                onClick={() => setTaskView('board')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${taskView === 'board' ? 'bg-[#111111] text-white' : 'text-[#666666] hover:bg-[#F5F5F5]'}`}
                data-testid="rt-view-board"
              >
                <Columns size={14} /> Board
              </button>
              <button
                onClick={() => setTaskView('calendar')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${taskView === 'calendar' ? 'bg-[#111111] text-white' : 'text-[#666666] hover:bg-[#F5F5F5]'}`}
                data-testid="rt-view-calendar"
              >
                <CalendarDays size={14} /> Calendar
              </button>
            </div>

            {taskView === 'board' ? (
              <RestaurantTaskBoard
                tasks={visibleTasks}
                onTaskClick={setTaskModal}
                onStatusChange={handleTaskStatusChange}
              />
            ) : (
              <RestaurantTaskCalendar tasks={visibleTasks} onTaskClick={setTaskModal} />
            )}

            {visibleTasks.length === 0 && (
              <div className="bg-white border border-[#E0E0E0] rounded-lg p-10 text-center text-[#666666]" data-testid="rt-empty-state">
                <LayoutGrid size={36} className="mx-auto text-[#E0E0E0] mb-3" />
                No restaurant tasks yet.
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {showModal && (
        <RestaurantFormModal
          open={showModal}
          onOpenChange={setShowModal}
          editing={editing}
          onSaved={() => { setShowModal(false); fetchData(); }}
        />
      )}

      {taskModal !== undefined && (
        <RestaurantTaskModal
          task={taskModal}
          restaurants={restaurants}
          users={users}
          canEdit={canManage}
          currentUser={user}
          onClose={() => setTaskModal(undefined)}
          onSaved={() => { setTaskModal(undefined); fetchData(); }}
        />
      )}
    </DashboardLayout>
  );
}
